import { cn } from "@/lib/utils";

export interface SegmentedChoice<K extends string> {
  key: K;
  label: string;
}

/**
 * Two or three ways to read the same list, side by side in one box, the one in
 * force pressed: the Terminology panel's "Terms only" and "With details".
 *
 * Not `Tabs`: tabs move between lists of different things, and each has its
 * own count. Here the list stays the same and only how much of each item shows
 * changes, so the choices are pressed buttons in a named group.
 */
export function Segmented<K extends string>({
  label,
  choices,
  current,
  onChoose,
  className,
}: {
  /** What the choices change, as a reader hears the group: "Table view". */
  label: string;
  choices: readonly SegmentedChoice<K>[];
  current: K;
  onChoose: (key: K) => void;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "inline-flex rounded-[10px] border border-border bg-card p-[2px]",
        className,
      )}
    >
      {choices.map((choice) => {
        const on = choice.key === current;
        return (
          <button
            key={choice.key}
            type="button"
            aria-pressed={on}
            onClick={() => {
              if (!on) onChoose(choice.key);
            }}
            className={cn(
              "cursor-pointer rounded-[8px] px-2.5 py-[5px] text-[11.5px] font-semibold whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none",
              on && "bg-muted text-primary hover:text-primary",
            )}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}
